import type { FrequencyEnumValue } from './models';

export interface CatchUpBillingCycleSale {
  cycleIndex: number;
  cycleLabel: string;
  billingDate: Date;
  periodEndDate: Date;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  cyclesIncluded?: number;
  grouped?: boolean;
}

export interface CatchUpBillingCyclesPreview {
  sales: CatchUpBillingCycleSale[];
  nextBillingDate: Date | null;
  grouped: boolean;
  catchUpCycleCount: number;
  cycleLabels: string[];
  regularCycleLabel: string | null;
}

interface CatchUpCycle {
  periodStart: Date;
  periodEnd: Date;
  label: string;
}

function startOfLocalDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/** Como {@code LocalDate#plusMonths}: si el mes destino no tiene el día, usa su último día. */
function addMonthsToDate(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const day = Math.min(date.getDate(), daysInMonth(target.getFullYear(), target.getMonth()));
  return startOfLocalDay(new Date(target.getFullYear(), target.getMonth(), day));
}

function addYearsToDate(date: Date, years: number): Date {
  return addMonthsToDate(date, years * 12);
}

export function resolveBillingDayOfMonth(
  billingDayOfMonth: number | null | undefined,
  collectionStartDate: Date | null,
  fallbackDate: Date
): number {
  if (billingDayOfMonth != null && billingDayOfMonth >= 1 && billingDayOfMonth <= 31) {
    return billingDayOfMonth;
  }
  if (collectionStartDate != null) {
    return collectionStartDate.getDate();
  }
  return fallbackDate.getDate();
}

function adjustToBillingDay(date: Date, billingDayOfMonth: number | null): Date {
  if (billingDayOfMonth == null) {
    return startOfLocalDay(date);
  }
  const y = date.getFullYear();
  const m = date.getMonth();
  const day = Math.min(Math.max(billingDayOfMonth, 1), daysInMonth(y, m));
  return startOfLocalDay(new Date(y, m, day));
}

/** Misma regla que {@code SubscriptionService#computeNextBillingDate}. */
export function computeNextBillingDate(
  from: Date,
  frequency: FrequencyEnumValue,
  billingDayOfMonth: number | null | undefined
): Date {
  const start = startOfLocalDay(from);
  switch (frequency) {
    case 'DAILY':
      return startOfLocalDay(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1));
    case 'WEEKLY':
      return startOfLocalDay(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7));
    case 'BIWEEKLY':
      return startOfLocalDay(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 14));
    case 'MONTHLY':
      return adjustToBillingDay(addMonthsToDate(start, 1), billingDayOfMonth ?? null);
    case 'QUARTERLY':
      return adjustToBillingDay(addMonthsToDate(start, 3), billingDayOfMonth ?? null);
    case 'SEMIANNUALLY':
      return adjustToBillingDay(addMonthsToDate(start, 6), billingDayOfMonth ?? null);
    case 'ANNUALLY':
      return adjustToBillingDay(addYearsToDate(start, 1), billingDayOfMonth ?? null);
    case 'ONCE':
      return start;
    default:
      frequency satisfies never;
      return start;
  }
}

function formatDateOnly(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

export function formatCatchUpCycleLabel(periodStart: Date, frequency: FrequencyEnumValue): string {
  switch (frequency) {
    case 'DAILY':
      return `Venta del día ${formatDateOnly(periodStart)}`;
    case 'WEEKLY':
      return `Venta de la semana del ${formatDateOnly(periodStart)}`;
    case 'BIWEEKLY':
      return `Venta de la quincena del ${formatDateOnly(periodStart)}`;
    case 'MONTHLY': {
      const raw = periodStart.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
      const monthName = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '';
      return `Venta del mes ${monthName}`;
    }
    case 'QUARTERLY': {
      const quarter = Math.floor(periodStart.getMonth() / 3) + 1;
      return `Venta del trimestre Q${quarter} ${periodStart.getFullYear()}`;
    }
    case 'SEMIANNUALLY': {
      const semester = Math.floor(periodStart.getMonth() / 6) + 1;
      return `Venta del semestre S${semester} ${periodStart.getFullYear()}`;
    }
    case 'ANNUALLY':
      return `Venta del año ${periodStart.getFullYear()}`;
    case 'ONCE':
      return `Venta del ciclo del ${formatDateOnly(periodStart)}`;
    default:
      frequency satisfies never;
      return `Venta del ciclo del ${formatDateOnly(periodStart)}`;
  }
}

function formatRegularCycleLabel(periodStart: Date, frequency: FrequencyEnumValue): string {
  const base = formatCatchUpCycleLabel(periodStart, frequency).replace(/^Venta del /, '');
  return `Venta del ciclo en curso (${base})`;
}

function periodEndBeforeNextCycle(
  periodStart: Date,
  frequency: FrequencyEnumValue,
  billingDayOfMonth: number | null | undefined
): Date {
  const next = computeNextBillingDate(periodStart, frequency, billingDayOfMonth);
  return startOfLocalDay(new Date(next.getFullYear(), next.getMonth(), next.getDate() - 1));
}

export interface CatchUpBillingState {
  catchUpCycles: CatchUpCycle[];
  currentCycleStart: Date | null;
}

/**
 * Ciclos completos vencidos y ciclo en curso.
 * Recorre las fechas de renovación desde la fecha de inicio con la misma regla del backend.
 */
export function resolveCatchUpBillingState(params: {
  collectionStartDate: Date | null;
  billingDayOfMonth: number | null | undefined;
  frequency: FrequencyEnumValue;
  asOf?: Date;
}): CatchUpBillingState {
  const asOf = startOfLocalDay(params.asOf ?? new Date());
  const frequency = params.frequency;
  if (
    params.collectionStartDate == null ||
    frequency === 'ONCE' ||
    !frequency
  ) {
    return { catchUpCycles: [], currentCycleStart: params.collectionStartDate };
  }

  const collectionStart = startOfLocalDay(params.collectionStartDate);
  if (!collectionStart.getTime() || collectionStart.getTime() >= asOf.getTime()) {
    return { catchUpCycles: [], currentCycleStart: collectionStart };
  }

  let cursor = collectionStart;
  const catchUpCycles: CatchUpCycle[] = [];

  while (true) {
    const next = computeNextBillingDate(cursor, frequency, params.billingDayOfMonth);
    if (next.getTime() <= cursor.getTime()) {
      return { catchUpCycles, currentCycleStart: cursor };
    }
    if (next.getTime() > asOf.getTime()) {
      return { catchUpCycles, currentCycleStart: cursor };
    }
    catchUpCycles.push({
      periodStart: cursor,
      periodEnd: periodEndBeforeNextCycle(cursor, frequency, params.billingDayOfMonth),
      label: formatCatchUpCycleLabel(cursor, frequency)
    });
    cursor = next;
  }
}

/** @deprecated Usar {@link resolveCatchUpBillingState}. */
export function resolveCatchUpMonths(params: {
  collectionStartDate: Date | null;
  billingDayOfMonth: number | null | undefined;
  frequency: FrequencyEnumValue;
  asOf?: Date;
}): Array<{ year: number; month: number; periodStart: Date; periodEnd: Date; label: string }> {
  const state = resolveCatchUpBillingState(params);
  return state.catchUpCycles.map((cycle) => ({
    year: cycle.periodStart.getFullYear(),
    month: cycle.periodStart.getMonth(),
    periodStart: cycle.periodStart,
    periodEnd: cycle.periodEnd,
    label: cycle.label
  }));
}

export function catchUpCyclesUnitLabel(frequency: FrequencyEnumValue | undefined, count = 2): string {
  const singular = count === 1;
  switch (frequency) {
    case 'DAILY':
      return singular ? 'día' : 'días';
    case 'WEEKLY':
      return singular ? 'semana' : 'semanas';
    case 'BIWEEKLY':
      return singular ? 'quincena' : 'quincenas';
    case 'MONTHLY':
      return singular ? 'mes' : 'meses';
    case 'QUARTERLY':
      return singular ? 'trimestre' : 'trimestres';
    case 'SEMIANNUALLY':
      return singular ? 'semestre' : 'semestres';
    case 'ANNUALLY':
      return singular ? 'año' : 'años';
    case 'ONCE':
    case undefined:
      return singular ? 'ciclo' : 'ciclos';
    default:
      frequency satisfies never;
      return singular ? 'ciclo' : 'ciclos';
  }
}

/**
 * Simula ventas retroactivas al crear una suscripción con fecha en el pasado.
 * El ciclo en curso no se incluye aquí (se crea al final).
 */
export function previewCatchUpBillingCycles(params: {
  collectionStartDate: Date | null;
  billingDayOfMonth: number | null | undefined;
  frequency: FrequencyEnumValue;
  asOf?: Date;
  groupCatchUp?: boolean;
  amounts: { subtotal: number; tax: number; discount: number; total: number };
}): CatchUpBillingCyclesPreview {
  const frequency = params.frequency;
  const state = resolveCatchUpBillingState({
    collectionStartDate: params.collectionStartDate,
    billingDayOfMonth: params.billingDayOfMonth,
    frequency,
    asOf: params.asOf
  });
  const catchUpCycles = state.catchUpCycles;
  const currentCycleStart = state.currentCycleStart;

  if (catchUpCycles.length === 0 || currentCycleStart == null) {
    return {
      sales: [],
      nextBillingDate: null,
      grouped: false,
      catchUpCycleCount: 0,
      cycleLabels: [],
      regularCycleLabel: null
    };
  }

  const cycleLabels = catchUpCycles.map((cycle) => cycle.label);
  const regularCycleLabel = formatRegularCycleLabel(currentCycleStart, frequency);
  const grouped = !!params.groupCatchUp;
  const nextBillingDate = computeNextBillingDate(
    currentCycleStart,
    frequency,
    params.billingDayOfMonth ?? null
  );
  const unitLabel = catchUpCyclesUnitLabel(frequency, catchUpCycles.length);

  if (grouped) {
    const first = catchUpCycles[0];
    const last = catchUpCycles[catchUpCycles.length - 1];
    const multiplier = catchUpCycles.length;
    return {
      grouped: true,
      catchUpCycleCount: catchUpCycles.length,
      cycleLabels,
      regularCycleLabel,
      nextBillingDate,
      sales: [
        {
          cycleIndex: 1,
          cycleLabel: `Venta agrupada (${catchUpCycles.length} ${unitLabel})`,
          billingDate: first.periodStart,
          periodEndDate: last.periodEnd,
          subtotal: params.amounts.subtotal * multiplier,
          tax: params.amounts.tax * multiplier,
          discount: params.amounts.discount * multiplier,
          total: params.amounts.total * multiplier,
          cyclesIncluded: multiplier,
          grouped: true
        }
      ]
    };
  }

  return {
    grouped: false,
    catchUpCycleCount: catchUpCycles.length,
    cycleLabels,
    regularCycleLabel,
    nextBillingDate,
    sales: catchUpCycles.map((cycle, index) => ({
      cycleIndex: index + 1,
      cycleLabel: cycle.label,
      billingDate: cycle.periodStart,
      periodEndDate: cycle.periodEnd,
      ...params.amounts
    }))
  };
}

export function requiresCatchUpConfirmation(preview: CatchUpBillingCyclesPreview): boolean {
  return preview.catchUpCycleCount > 0;
}
